/**
 * <customer-login-form> toggles between the login and password recovery panels rendered by the
 * "customer-login-form" snippet, and decides where the customer is redirected once logged in.
 *
 * Attributes:
 * - account-path: localized /account path, used to never redirect back to an account page
 * - return-to-current-url: redirect back to the current URL (the form replaces a locked page)
 */
class CustomerLoginForm extends HTMLElement {
  static RECOVER_HASH = '#recover';

  connectedCallback() {
    this.loginPanel = this.querySelector('#customer-login-panel');
    this.recoverPanel = this.querySelector('#customer-recover-panel');

    this._onClick = this._onClick.bind(this);
    this._onHashChange = this._onHashChange.bind(this);

    this.addEventListener('click', this._onClick);
    window.addEventListener('hashchange', this._onHashChange);

    this._setReturnTo();

    // Shopify redirects to /account/login#recover after a recover request
    if (window.location.hash === CustomerLoginForm.RECOVER_HASH) {
      this._showPanel(this.recoverPanel, { focus: false, updateHash: false });
    }

    // Move focus to a server-rendered error so it is announced and the customer can act on it right away
    this._visiblePanel()?.querySelector('[data-form-error]')?.focus();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    window.removeEventListener('hashchange', this._onHashChange);
  }

  _onClick(event) {
    const trigger = event.target.closest('[data-action="show-panel"]');

    if (!trigger || !this.contains(trigger)) {
      return;
    }

    this._showPanel(this.querySelector(`#${trigger.getAttribute('aria-controls')}`));
  }

  _onHashChange() {
    const panel = window.location.hash === CustomerLoginForm.RECOVER_HASH ? this.recoverPanel : this.loginPanel;
    this._showPanel(panel, { updateHash: false });
  }

  _showPanel(panel, { focus = true, updateHash = true } = {}) {
    if (!panel || !panel.hidden) {
      return;
    }

    [this.loginPanel, this.recoverPanel].forEach((element) => {
      element.hidden = element !== panel;
    });

    if (updateHash) {
      const hash = panel === this.recoverPanel ? CustomerLoginForm.RECOVER_HASH : '';
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${hash}`);
    }

    if (focus) {
      panel.querySelector('[data-panel-heading]')?.focus();
    }
  }

  _visiblePanel() {
    return [this.loginPanel, this.recoverPanel].find((panel) => panel && !panel.hidden);
  }

  _setReturnTo() {
    const input = this.querySelector('[data-return-to]');

    if (!input) {
      return;
    }

    if (this.hasAttribute('return-to-current-url')) {
      input.value = `${window.location.pathname}${window.location.search}`;
    } else {
      input.value = this._referrerPath() ?? window.Shopify?.routes?.root ?? '/';
    }
  }

  /**
   * Returns the path of the page the customer came from, as long as it is on this store and not an account page
   * (sending a customer back to the login or register page after logging in would be a dead end).
   */
  _referrerPath() {
    if (!document.referrer) {
      return null;
    }

    let referrer;

    try {
      referrer = new URL(document.referrer);
    } catch {
      return null;
    }

    const accountPath = this.getAttribute('account-path') || '/account';

    if (referrer.origin !== window.location.origin || referrer.pathname.startsWith(accountPath)) {
      return null;
    }

    return `${referrer.pathname}${referrer.search}`;
  }
}

if (!window.customElements.get('customer-login-form')) {
  window.customElements.define('customer-login-form', CustomerLoginForm);
}
